import React from "react";
import {
  Container,
  Row,
  Col,
  Button,
  Badge,
  Image,
} from "react-bootstrap";

import { LuArrowUpRight } from "react-icons/lu";
import { GoArrowUpRight } from "react-icons/go";
import { useNavigate } from "react-router-dom";
import { brandLogo, brandName } from "../Constants/content";
import {
  primaryColor,
  secondaryColor,
  textDarkColor,
} from "../Constants/Colors";

const JobDescription = ({
  jobTitle,
  location,
  department,
  jobType,
  experienceLevel,
  roleDescription,
  responsibilities,
  skills,
  requirements,
  benefits,
  onApply,
  applyUrl,
}) => {
  const navigate = useNavigate();
  const radius = 60; // Radius for the circular text

  // Replace {brandName} placeholder in role description
  const processedRoleDescription = roleDescription?.replace(
    /{brandName}/g,
    brandName,
  );

  return (
    <Container className="py-5">
      {/* Job Title Section */}
      <div className="text-center mb-4">
        <Badge bg="primary" className="mb-2">
          Career
        </Badge>
        <h2 className="fw-bold">{jobTitle}</h2>
        <p className="text-muted">
          Location: <strong>{location}</strong>
        </p>
        <Button
          style={{
            backgroundColor: secondaryColor,
            color: textDarkColor,
            border: "none",
            boxShadow: "0 4px 20px rgba(0, 157, 255, 0.3)",
          }}
          className="rounded-pill my-2 px-4 arrowLauncher-container"
          onClick={onApply}
        >
          Apply to this Job <LuArrowUpRight className="fs-5 arrowLauncher" />
        </Button>
      </div>

      {/* About Company */}
      <Row className="mt-5">
        <Col lg={12} className="m-auto">
          <h4 className="fw-bold">
            Who We Are – <span className="text-primary">{brandName}</span>
          </h4>
          <p>
            At {brandName}, we believe innovation starts with people. We're a
            passionate team building purposeful digital solutions that drive
            impact. From startups to enterprises, we partner with clients to
            deliver beautifully engineered experiences.
          </p>
          <p>
            We foster a growth-oriented culture where talent thrives, ideas are
            celebrated, and learning never stops. Whether you're a designer,
            developer, or strategist—you matter. Mission, ideas, and fun all
            live and shape the digital future, together.
          </p>
        </Col>
        <Col
          lg={7}
          className="m-auto my-5"
          style={{
            height: "400px",
            overflowY: "scroll",
            scrollbarWidth: "thin",
            scrollbarColor: `${primaryColor} transparent`,
          }}
        >
          {/* About the Role */}
          {processedRoleDescription && (
            <>
              <h5 className="fw-bold mt-4">About The Role</h5>
              <p>{processedRoleDescription}</p>
            </>
          )}

          {/* Responsibilities */}
          {responsibilities && responsibilities.length > 0 && (
            <>
              <h5 className="fw-bold mt-4">What You'll Do</h5>
              <ul className="ps-3">
                {responsibilities.map((responsibility, index) => (
                  <li key={index}>{responsibility}</li>
                ))}
              </ul>
            </>
          )}

          {/* Skills */}
          {skills && skills.length > 0 && (
            <>
              <h5 className="fw-bold mt-4">Skills You Need</h5>
              <ul>
                {skills.map((skill, index) => (
                  <li key={index}>{skill}</li>
                ))}
              </ul>
            </>
          )}

          {/* Requirements */}
          {requirements && requirements.length > 0 && (
            <>
              <h5 className="fw-bold mt-4">You're a Great Fit If You</h5>
              <ul>
                {requirements.map((requirement, index) => (
                  <li key={index}>{requirement}</li>
                ))}
              </ul>
            </>
          )}

          {/* Benefits */}
          {benefits && benefits.length > 0 && (
            <>
              <h5 className="fw-bold mt-4">Why Work With Us?</h5>
              <ul>
                {benefits.map((benefit, index) => (
                  <li key={index}>{benefit}</li>
                ))}
              </ul>
            </>
          )}
        </Col>

        {/* Sidebar */}
        <Col lg={4} className="mt-4 my-lg-5 m-auto">
          <div
            className="shadow-none ps-4 py-3 pe-5"
            style={{ borderLeft: "1px solid rgb(192, 185, 185)" }}
          >
            <h6 className="fw-bold mb-3" style={{ color: primaryColor }}>
              Job Information
            </h6>

            {location && (
              <div className="mb-2">
                <h6 className="text-muted fw-bold">Location:</h6>
                <p
                  style={{
                    fontSize: "14px",
                    borderBottom: "1px solid rgb(197, 192, 192)",
                  }}
                >
                  {location}, India
                </p>
              </div>
            )}

            {department && (
              <div className="mb-2">
                <h6 className="text-muted fw-bold">Department:</h6>
                <p
                  style={{
                    fontSize: "14px",
                    borderBottom: "1px solid rgb(197, 192, 192)",
                  }}
                >
                  {department}
                </p>
              </div>
            )}

            {jobType && (
              <div className="mb-2">
                <h6 className="text-muted fw-bold">Job Type:</h6>
                <p
                  style={{
                    fontSize: "14px",
                    borderBottom: "1px solid rgb(197, 192, 192)",
                  }}
                >
                  {jobType}
                </p>
              </div>
            )}

            {experienceLevel && (
              <div className="mb-2">
                <h6 className="text-muted fw-bold">Experience Level:</h6>
                <p
                  style={{
                    fontSize: "14px",
                    borderBottom: "1px solid rgb(197, 192, 192)",
                  }}
                >
                  {experienceLevel}
                </p>
              </div>
            )}
          </div>
        </Col>
      </Row>

      <Row>
        <Col xs={12} md={10} className="m-auto mt-2 mb-5">
          <div
            style={{
              padding: "2px",
              borderRadius: "20px",
              background: `linear-gradient(to right, ${secondaryColor}, ${primaryColor})`,
            }}
          >
            <Row
              className="align-items-center p-2 p-md-1 m-auto justify-content-center py-3"
              style={{
                backgroundColor: "#c6e0f7",
                borderRadius: "18px",
              }}
            >
              <Col
                xs={3}
                className="d-flex justify-content-center align-items-center"
              >
                <Image
                  src={brandLogo}
                  style={{ width: "120px", height: "80px" }}
                />
              </Col>
              <Col md={6} className="text-start py-3">
                <h5>
                  Didn't find the role you're looking for? Let's still connect.
                </h5>
                <p className="p-0 m-0 mt-lg-3 mt-3">
                  Send your resume + portfolio to hr@idealcorp.in
                </p>
                <p className="p-0 m-0 mt-lg-3 mt-3">
                  Subject: [Your Role] – [Your Name]
                </p>
                <p className="p-0 m-0 mt-lg-3 mt-3">
                  Or Hit &nbsp;&nbsp;
                  <a
                    href={applyUrl}
                    style={{ color: textDarkColor }}
                    className="arrowLauncher-container"
                  >
                    Apply Now <LuArrowUpRight className="fs-5 arrowLauncher" />
                  </a>
                </p>
              </Col>
              <Col md={3} className="text-end mt-3 mt-md-0">
                <div
                  style={{
                    position: "relative",
                    width: radius * 2 + 40,
                    height: radius * 2 + 40,
                    margin: "auto",
                  }}
                >
                  {/* Static Center Icon */}
                  <div
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      zIndex: 2,
                    }}
                  >
                    <GoArrowUpRight
                      size={38}
                      color={textDarkColor}
                      style={{ cursor: "pointer" }}
                      onClick={() => navigate("/careers")}
                    />
                  </div>

                  {/* Rotating SVG Circle with Text */}
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      animation: "spin 10s linear infinite",
                      zIndex: 1,
                    }}
                  >
                    <svg width="100%" height="100%" viewBox="0 0 250 250">
                      <defs>
                        <path
                          id="circlePath"
                          d={`
                                           M 125,125
                                           m -${radius},0
                                           a ${radius},${radius} 0 1,1 ${
                                             radius * 2
                                           },0
                                           a ${radius},${radius} 0 1,1 -${
                                             radius * 2
                                           },0
                                         `}
                        />
                      </defs>
                      <text
                        fill={textDarkColor}
                        fontSize="20"
                        fontFamily="sans-serif"
                      >
                        <textPath href="#circlePath" startOffset="0%">
                          Get in touch • Hire us • Contact us • Connect with us
                          •
                        </textPath>
                      </text>
                    </svg>
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default JobDescription;
